import { computed } from "vue";
import { useFuse } from "@vueuse/integrations/useFuse";
import { FUSE_OPTIONS, MIN_FUZZY_QUERY_LENGTH, rankFuzzyItems } from "./rank-fuzzy-items.js";
import { filterBySearch } from "./filter-items.js";
import { groupItems } from "./group-items.js";
import { markFirstResult } from "./mark-first-result.js";
import { searchStrings } from "./search-helpers.js";
import { isVisibleForQuery } from "./visibility.js";

/**
 * Search + group pipeline for navigation containers. Routes a query to one of two
 * matchers and returns `groupedItems`: the fuzzy pipeline (an exact word-prefix
 * gate, falling back to fuse.js + a deterministic re-rank only when nothing
 * matches exactly) in fuzzy mode above the minimum match length, otherwise
 * substring filtering followed by section-header grouping.
 *
 * @param {Object} options
 * @param {import('vue').Ref<Array>} options.items
 * @param {import('vue').Ref<string|undefined>} options.search
 * @param {import('vue').Ref<boolean>} options.noFilter - pre-coerced; callers normalise the prop
 * @param {(item: any) => string|undefined} options.getItemTitle
 * @param {import('vue').Ref<number|string|undefined>} [options.maxResults] - cap on
 *   the number of matched (non-header) items rendered for a query. Undefined/0 = no
 *   cap. Guards against pathological renders when a huge list meets a broad query.
 * @param {import('vue').Ref<boolean>} [options.fuzzySearch] - pre-coerced; switches
 *   the query filter to the fuzzy pipeline above.
 * @param {import('vue').Ref<boolean>} [options.highlightFirstResult] - pre-coerced;
 *   while a query is active, mark the first selectable result as the combobox's
 *   pending choice (`props.highlighted`, rendered by sp-list-item as the
 *   hover-style hint). The host pairs this with committing that row on Enter.
 * @param {import('vue').Ref<boolean>} [options.highlightIdleFirstResult] - pre-coerced;
 *   extend the marking to the idle (no-query) list, for hosts that highlight in
 *   browse mode too.
 * @param {import('vue').Ref<string|undefined>} [options.sectionOrder] - fuzzy-mode
 *   section ordering: "source" (default) keeps the item-set order; "relevance"
 *   orders sections by their best-ranked row, ungrouped rows leading.
 * @param {import('vue').Ref<boolean>} [options.noSubstringFallback] - pre-coerced;
 *   in fuzzy mode, route every query — including single-character and all-digit
 *   queries — through the exact word-boundary pass (with the fuzzy fallback)
 *   instead of substring filtering.
 *
 * An item's `visibility` gates it on the query state before either matcher runs: `"search"` rows
 * render only while a trimmed query is active, `"browse"` rows only while none is.
 */
export function useQuantumSelectItems({
  items,
  search,
  noFilter,
  getItemTitle,
  maxResults,
  fuzzySearch,
  highlightFirstResult,
  highlightIdleFirstResult,
  selectableBranches,
  sectionOrder,
  noSubstringFallback,
}) {
  const hasQuery = computed(() => (search.value ?? "").trim().length > 0);

  const visibleItems = computed(() => (items.value ?? []).filter((item) => isVisibleForQuery(item, hasQuery.value)));

  // fuse indexes the searchable entries only in fuzzy mode; the non-fuzzy pickers
  // build nothing. Header, pinned, and fallback rows never enter the index. Each
  // item contributes one entry per search string (title + each keyword, or a lone
  // `searchText`): fuse's weighted multi-key scoring inflates a keyword-only
  // match past the legacy cutoffs, while per-string entries score every string
  // the way the legacy autocomplete scored `matchValue`. The item's best string
  // wins.
  const searchEntries = computed(() => {
    if (!fuzzySearch?.value) {
      return [];
    }
    return visibleItems.value
      .filter((item) => item && !item.header && !item.pinned && !item.fallback)
      .flatMap((item) => {
        const title = getItemTitle(item) ?? "";
        return searchStrings(item, getItemTitle).map((text) => ({ item, title, text }));
      });
  });

  // All-digit queries never fuzzy-match: an off-by-one digit is one edit away
  // and would surface the wrong record. Fuse sees an empty query; the numeric
  // path in `rankFuzzyItems` matches by substring instead. Under
  // `noSubstringFallback` there is no numeric path, so digit queries stay in
  // the fuse index like any other. Queries below the fuzzy minimum always blank
  // out — fuse's own `minMatchCharLength` rejects them too.
  const fuseQuery = computed(() => {
    const trimmed = (search.value ?? "").trim();
    if (trimmed.length < MIN_FUZZY_QUERY_LENGTH) {
      return "";
    }
    if (!noSubstringFallback?.value && /^\d+$/.test(trimmed)) {
      return "";
    }
    return trimmed;
  });

  const { results: fuseResults } = useFuse(fuseQuery, searchEntries, {
    fuseOptions: FUSE_OPTIONS,
    matchAllWhenSearchEmpty: false,
  });

  const groupedItems = computed(() => {
    // Query activity is derived from the trimmed value, so a whitespace-only
    // query keeps the idle grouped list.
    const query = (search.value ?? "").trim();
    let result;
    // Fuzzy ranking needs at least fuse's min match length; a shorter query falls
    // back to substring filtering so a single character still lists matches —
    // unless `noSubstringFallback` sends every non-empty query through the
    // fuzzy pipeline's exact word-boundary pass.
    const minFuzzyLength = noSubstringFallback?.value ? 1 : MIN_FUZZY_QUERY_LENGTH;
    if (fuzzySearch?.value && !noFilter.value && query.length >= minFuzzyLength) {
      result = rankFuzzyItems(visibleItems.value, query, fuseResults.value, getItemTitle, maxResults?.value, {
        sectionOrder: sectionOrder?.value,
        noSubstringFallback: noSubstringFallback?.value,
      });
    } else {
      const filtered = filterBySearch(visibleItems.value, query, noFilter.value, getItemTitle, maxResults?.value);
      result = groupItems(filtered, getItemTitle);
    }

    if (highlightFirstResult?.value && (query || highlightIdleFirstResult?.value)) {
      result = markFirstResult(result, { selectableBranches: selectableBranches?.value });
    }
    return result;
  });

  return { groupedItems };
}
